import { CellValueChangedEvent, ProcessDataFromClipboardParams } from 'ag-grid-community';
import { dropRightWhile, forEach, uniqueId } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AgGridTable, Button } from 'core/components';
import { AgGridClientTableProvider, useFormContext } from 'core/states';
import { AgGridDataRow } from 'core/types';
import { Variant } from 'theme/enums';

import { getColumnDefs } from './columns';

export const Table = () => {
  const { t } = useTranslation();

  const { control, errors } = useFormContext();

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'variations',
  });

  const handleAddRow = () => append({ id: uniqueId(), length: null, quantity: 1 });

  const handleRemoveRow = useCallback(
    (id: string | number) => {
      const index = fields.findIndex((field) => field.id === id);

      remove(index);
    },
    [fields, remove]
  );

  const columnDefs = useMemo(
    () => getColumnDefs((errors as any).variations, handleRemoveRow),
    [errors, handleRemoveRow]
  );

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      floatingFilter: false,
      sortable: false,
      resizable: false,
      editable: true,
      flex: 1,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const processDataFromClipboard = useCallback(
    ({ data: rowsFromClipboard, api }: ProcessDataFromClipboardParams): string[][] | null => {
      let data = [...rowsFromClipboard];

      data = dropRightWhile(data, (row) => row.length === 1 && row[0] === '');

      const lastIndex = api?.getDisplayedRowCount() - 1;
      const focusedCell = api?.getFocusedCell();
      const focusedIndex = focusedCell?.rowIndex;

      if (focusedIndex + data.length - 1 <= lastIndex) {
        return data;
      }

      const resultLastIndex = focusedIndex + (data.length - 1);
      const numRowsToAdd = resultLastIndex - lastIndex;

      const rowsToAdd = data.slice(data.length - numRowsToAdd).map((row) => {
        const rowObject: AgGridDataRow = { id: uniqueId() };

        let currentColumn = focusedCell?.column;

        forEach(row, (item) => {
          if (!currentColumn) {
            return;
          }

          rowObject[currentColumn.getColDef().field] = item;
          currentColumn = api?.getDisplayedColAfter(currentColumn);
        });

        return rowObject;
      });

      append(rowsToAdd);
      api.applyTransaction({ add: rowsToAdd });

      return data;
    },
    [append]
  );

  const onCellValueChanged = useCallback(
    (_, { colDef: { field }, data, newValue, rowIndex }: CellValueChangedEvent) => {
      data[field] = newValue;

      update(rowIndex, data);

      return { updatedRow: data, options: { redrawRows: false } };
    },
    [update]
  );

  return (
    <AgGridClientTableProvider
      tableId="variationTable"
      entityType="Variation"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={fields}
      isDataLoading={false}
      loadingPercentage={100}
      dataCurrentPage={1}
      onCellValueChanged={onCellValueChanged}
      processDataFromClipboard={processDataFromClipboard}
      rowHeight={40}
      sideBar={{}}
      hasTopActions={false}
      hasRowSelection={false}
    >
      <AgGridTable />
      <Button
        text={t('buttonLabelAddNewLine')}
        onClick={handleAddRow}
        variant={Variant.SOFT}
        sx={{ width: 'fit-content' }}
      />
    </AgGridClientTableProvider>
  );
};
